import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import type { ContentItem } from '../types/content';

interface FaqEntry {
  question: string;
  answer: string;
}

interface ContactInfo {
  phone?: string;
  email?: string;
  address?: string;
  hours?: string;
}

interface PageContent {
  title: string;
  description: string;
  faqs: FaqEntry[];
  contact: ContactInfo;
}

const defaultContent: PageContent = {
  title: 'Support',
  description: 'We\'re here to help. Find answers to common questions or get in touch with our team.',
  faqs: [],
  contact: {},
};

function parseContent(item: ContentItem | undefined): any {
  if (!item?.content) return null;
  try {
    return typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
  } catch {
    return item.content;
  }
}

function getItem(section: any, title: string): ContentItem | undefined {
  return section?.content_items?.find(
    (i: ContentItem) => i.title === title && i.status === 'Published'
  );
}

export default function Support() {
  const [pageContent, setPageContent] = useState<PageContent>(defaultContent);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const { data: sections, error } = await supabase
        .from('content_sections')
        .select('id, title, content_items (*)')
        .eq('page', 'support');

      if (error) throw error;

      if (sections) {
        const headerSection = sections.find(s => s.title === 'Page Header');
        const faqSection = sections.find(s => s.title === 'FAQs');
        const contactSection = sections.find(s => s.title === 'Contact Support');

        const titleItem = getItem(headerSection, 'Title');
        const descItem = getItem(headerSection, 'Description');
        const faqsItem = getItem(faqSection, 'FAQ List');
        const contactItem = getItem(contactSection, 'Contact Details');

        const parsedFaqs = parseContent(faqsItem);
        const parsedContact = parseContent(contactItem);

        setPageContent({
          title: parseContent(titleItem) || defaultContent.title,
          description: parseContent(descItem) || defaultContent.description,
          faqs: Array.isArray(parsedFaqs) ? parsedFaqs : [],
          contact: parsedContact && typeof parsedContact === 'object' ? parsedContact : {},
        });
      }
    } catch (err) {
      console.error('Error fetching support content:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasFaqs = pageContent.faqs.length > 0;
  const hasContact = Object.values(pageContent.contact).some(Boolean);

  if (loading) {
    return (
      <div className="pt-20 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative bg-golf-gray text-white py-16 sm:py-20 md:py-28">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1325681/pexels-photo-1325681.jpeg?auto=compress&w=1200"
            alt="Golf course"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold mb-4 sm:mb-6">
              {pageContent.title}
            </h1>
            <p className="text-lg sm:text-xl text-golf-sand">
              {pageContent.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      {hasFaqs && (
        <section className="py-12 sm:py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-golf-gray text-center mb-10 sm:mb-14">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {pageContent.faqs.map((faq, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-golf-gray pr-4">{faq.question}</span>
                      {openFaq === idx
                        ? <ChevronUpIcon className="w-5 h-5 text-golf-orange flex-shrink-0" />
                        : <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      }
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                        {faq.answer}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Contact */}
      {hasContact && (
        <section className={`py-12 sm:py-16 md:py-24 ${hasFaqs ? 'bg-gray-50' : 'bg-white'}`}>
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-golf-gray text-center mb-10 sm:mb-14">
                Get in Touch
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {pageContent.contact.phone && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 flex items-start gap-4 shadow-sm">
                    <div className="w-11 h-11 bg-golf-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <PhoneIcon className="w-5 h-5 text-golf-orange" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Phone</p>
                      <p className="text-golf-gray font-medium">{pageContent.contact.phone}</p>
                    </div>
                  </div>
                )}
                {pageContent.contact.email && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 flex items-start gap-4 shadow-sm">
                    <div className="w-11 h-11 bg-golf-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <EnvelopeIcon className="w-5 h-5 text-golf-orange" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Email</p>
                      <a
                        href={`mailto:${pageContent.contact.email}`}
                        className="text-golf-gray font-medium hover:text-golf-orange transition-colors"
                      >
                        {pageContent.contact.email}
                      </a>
                    </div>
                  </div>
                )}
                {pageContent.contact.address && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 flex items-start gap-4 shadow-sm">
                    <div className="w-11 h-11 bg-golf-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPinIcon className="w-5 h-5 text-golf-orange" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Address</p>
                      <p className="text-golf-gray font-medium">{pageContent.contact.address}</p>
                    </div>
                  </div>
                )}
              </div>
              {pageContent.contact.hours && (
                <p className="mt-8 text-center text-sm text-gray-500">{pageContent.contact.hours}</p>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Empty state */}
      {!hasFaqs && !hasContact && (
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 text-center text-gray-400">
            <p className="text-lg">Support content coming soon.</p>
          </div>
        </section>
      )}
    </div>
  );
}
